import { v4 as uuidv4 } from 'uuid';
import { ContextUtility } from "./context.utility";
import { RestUtility } from "./rest.utility";
import { UrlUtility } from "./url.utility";
import { CartGiftData, CartProductData } from '../models/cart.models';

// TODO: this cannot be included for node due to jQuery in one of the methods. Somehow make sure the jQuery using method is ignored and not included in the node build

/**
 * Adds a product to the current user's cart by loading the item detail page in a hidden iframe and triggering its built-in "Add to Cart" button, mimicking a normal browser add-to-cart flow. Browser-only (relies on jQuery/iframe) and intended as an alternative to {@link addProductToCart} when the REST payload approach cannot be used.
 * @param productCode The item code of the product to add to the cart.
 * @param productPageUrl The item detail page URL to load in the iframe. Defaults to "/ItemDetail".
 * @returns A promise that resolves once the item has been added (or was already present) in the cart.
 */
function addProductToCartByFrame(productCode: string, productPageUrl: string = "/ItemDetail") {
    return new Promise<void>((resolve, reject) => {
        // add product with the above product code to the cart
        let productPageUrlFull = UrlUtility.getUrlStringWithParams(productPageUrl, { iProductCode: productCode });
        let jFrame = jQuery("<iframe src='" + productPageUrlFull + "'></iframe>");
        jFrame.on("load", function () {
            let frameElement = <HTMLIFrameElement>jFrame[0];
            let contentWindow = frameElement.contentWindow;

            let jItemAdded = contentWindow.jQuery("[id$=_ItemAdded]");
            if (jItemAdded.length > 0) {
                // redirect to the appropriate cart page
                resolve();
            } else {
                // initiate an add to cart
                let jAddToCart = contentWindow.jQuery("[id$=_AddToCart]");
                jAddToCart[0].click();
            }
        });
        jFrame.appendTo("body");
        jFrame.hide();
    });
}

/**
 * Adds a product to a user's cart via the REST API, creating the cart and delivery address if needed, and updates the cart's pricing. Falls back to the currently selected iMIS user and the party's mailing address when not explicitly provided.
 * @param productCode The item code of the product to add to the cart.
 * @param id The ID of the party whose cart the product should be added to. Defaults to the currently selected iMIS ID from {@link ContextUtility.getSelectedImisId}.
 * @param productData Optional quantity, amount, and gift-status overrides for the product line. Omitted fields default to `null`/`false`.
 * @returns A promise resolving to the updated cart payload.
 */
async function addProductToCart(productCode: string, id?: string, productData?: CartProductData) {
    let selectedId = id || ContextUtility.getSelectedImisId();
    let finalProductData: CartProductData = Object.assign({
        quantity: null,
        amount: null,
        isGift: false
    }, productData);

    // get product item details
    let itemDetails: any = await RestUtility.restApiGetData("/api/item/" + productCode);
    let partyDetails: any = await RestUtility.restApiGetData("/api/party/" + selectedId);
    if (itemDetails == null || partyDetails == null) {
        throw new Error(`Failed to get item details for product code: ${productCode} and user with ID: ${selectedId}`);
    }

    // setup payload for cart
    // add delivery details if not provided
    let cartPayload = await getCartPayloadWithProduct(selectedId, productCode);
    if (cartPayload.ComboOrder.Order.Delivery.$values.length == 0) {
        // get the mailing address for the user
        let mailingAddress = partyDetails.Addresses.find(address => address.CommunicationPreferences.some(pref => pref.Reason == "mail"));
        if (mailingAddress == null) {
            throw new Error(`Failed to get mailing address for user with ID: ${selectedId}`);
        }

        cartPayload.ComboOrder.Order.Delivery.$values.push({
            Address: mailingAddress
        });
    }

    // update pricing for the newly added product to the existing cart
    await updateCartOrderPricing(cartPayload);

    // update pricing and quantity if available
    let isUpdated = updateProductPayloadQuantityAmount(cartPayload, productCode, finalProductData);
    if (isUpdated) {
        await updateCartOrderPricing(cartPayload);
    }

    cartPayload = await updateCart(cartPayload);
    return cartPayload;
}

/**
 * Adds a gift line item to a user's cart by delegating to {@link addProductToCart} with the gift flag set.
 * @param productCode The item code of the gift product to add to the cart.
 * @param id The ID of the party whose cart the gift should be added to. Defaults to the currently selected iMIS ID.
 * @param giftData Optional amount override for the gift line. Omitted amount defaults to `null`.
 * @returns A promise resolving to the updated cart payload.
 */
async function addGiftToCart(productCode: string, id?: string, giftData?: CartGiftData) {
    let finalizedGiftData: CartGiftData = Object.assign({
        amount: null
    }, giftData);
    return addProductToCart(productCode, id, Object.assign({ isGift: true }, finalizedGiftData));
}

/**
 * Persists a cart payload by sending it as a PUT request to the cart's REST endpoint.
 * @param cartPayload The full cart data object (must include a `CartId`) to save.
 * @returns A promise resolving to the updated cart data returned by the API.
 */
async function updateCart(cartPayload) {
    let cartId = cartPayload.CartId;
    let cartUpdateResponse: any = await RestUtility.restApiSendData("/api/cart/" + cartId, cartPayload, "PUT", true);
    if (cartUpdateResponse == null) {
        throw new Error(`Failed to update cart for cart ID: ${cartId}`);
    }

    return cartUpdateResponse;
}

/**
 * Retrieves the cart record(s) associated with the given user ID.
 * @param id The ID of the user whose cart should be retrieved; used for both the `userid` and `updatedby` query parameters.
 * @param rawResponse Whether to return the raw, unprocessed API response instead of the parsed data. Defaults to `false`.
 * @returns A promise resolving to the cart response data (shape depends on `rawResponse`).
 */
async function getCartById(id: string, rawResponse: boolean = false) {
    return RestUtility.restApiGetData("/api/cart", {
        userid: id, updatedby: id
    }, rawResponse);
}

/**
 * Removes a single product line item from a user's cart and updates the cart's pricing afterward.
 * @param productCode The item code of the product line to remove.
 * @param id The ID of the party whose cart should be modified. Defaults to the currently selected iMIS ID.
 * @returns A promise resolving to the updated cart data after the line item has been removed.
 */
async function deleteProductFromCart(productCode: string, id?: string) {
    let selectedId = id || ContextUtility.getSelectedImisId();
    let cartData = await ensureCartById(selectedId);
    let orderData = cartData.ComboOrder.Order;
    let { lineIndex, lineItem } = getLineItemByProductCode(orderData, productCode);
    if (lineItem == null) {
        throw new Error(`Failed to get line item for product code: ${productCode} and user with ID: ${selectedId}`);
    }

    orderData.Lines.$values.splice(lineIndex, 1);
    await updateCartOrderPricing(cartData);
    cartData = await updateCart(cartData);

    return cartData;
}

/**
 * Deletes the entire cart record for a user, if one exists.
 * @param id The ID of the user whose cart should be deleted.
 */
async function deleteCartById(id: string) {
    let cartResponse = await getCartById(id);
    let cartItem: any = cartResponse != null ? cartResponse.Items[0] : null;
    if (cartItem != null) {
        let cartId = cartItem.CartId;

        let isSuccess = await RestUtility.restApiDeleteData("/api/cart/" + cartId);
        if (!isSuccess) {
            throw new Error(`Failed to delete cart for user with ID: ${id} and cart ID: ${cartId}`);
        }
    }
}

/**
 * Retrieves the existing cart for a user, or creates a new empty cart record if one does not already exist.
 * @param id The ID of the user whose cart should be fetched or created.
 * @returns A promise resolving to the (existing or newly created) cart data.
 */
async function ensureCartById(id: string) {
    let cartData: any = null;
    let currentDate = new Date();

    let cartResponse: any = await getCartById(id, true);
    if (cartResponse == null) {
        throw new Error(`Failed to get cart for user with ID: ${id}`);
    }
    if (cartResponse.Items.$values.length > 0) {
        cartData = cartResponse.Items.$values[0];
    } else {
        // create an empty cart
        let addCartPayload = {
            $type: "Asi.Soa.Commerce.DataContracts.CartData, Asi.Contracts",
            UserId: id,
            ComboOrder: {
                $type: "Asi.Soa.Commerce.DataContracts.ComboOrderData, Asi.Contracts"
            },
            UpdateInformation: {
                $type: "Asi.Soa.Core.DataContracts.EntityUpdateInformationData, Asi.Contracts",
                CreatedBy: id,
                CreatedOn: currentDate,
                UpdatedBy: id,
                UpdatedOn: currentDate
            }
        };
        cartData = await RestUtility.restApiSendData("/api/cart", addCartPayload, "POST", true);
        if (cartData == null) {
            throw new Error(`Failed to create cart for user with ID: ${id}`);
        }
    }

    return cartData;
}

/**
 * Ensures a user's cart has an order object and contains a line item for the given product code, creating the order structure and/or line item as needed without persisting the changes.
 * @param id The ID of the user whose cart payload should be built.
 * @param productCode The item code of the product that must be present as a line item in the cart.
 * @returns A promise resolving to the cart data with the order and line item ensured (not yet saved).
 */
async function getCartPayloadWithProduct(id: string, productCode: string) {
    // ensure the cart object exists
    let cartData = await ensureCartById(id);
    let orderData = cartData.ComboOrder.Order;
    // have to check delivery property because by default an empty cart will always contain the Currency and Lines property
    if (orderData.Delivery == null) {
        // create the order object
        orderData = {
            "$type": "Asi.Soa.Commerce.DataContracts.OrderData, Asi.Contracts",
            BillToCustomerParty: {
                "$type": "Asi.Soa.Commerce.DataContracts.CustomerPartyData, Asi.Contracts",
                PartyId: id,
            },
            Delivery: {
                "$type": "Asi.Soa.Commerce.DataContracts.DeliveryDataCollection, Asi.Contracts",
                $values: []
            },
            Lines: {
                "$type": "Asi.Soa.Commerce.DataContracts.OrderLineDataCollection, Asi.Contracts",
                $values: []
            },
            OriginatorCustomerParty: {
                "$type": "Asi.Soa.Commerce.DataContracts.CustomerPartyData, Asi.Contracts",
                PartyId: id,
            },
            SoldToCustomerParty: {
                "$type": "Asi.Soa.Commerce.DataContracts.CustomerPartyData, Asi.Contracts",
                PartyId: id,
            }
        };

        cartData.ComboOrder.Order = orderData;
    }

    // ensure the line item is not already there
    let { lineItem } = getLineItemByProductCode(orderData, productCode);
    if (lineItem == null) {
        // add the line item to the cart
        let linePayload = {
            "$type": "Asi.Soa.Commerce.DataContracts.OrderLineData, Asi.Contracts",
            OrderLineId: uuidv4(),
            Item: {
                "$type": "Asi.Soa.Commerce.DataContracts.ItemSummaryData, Asi.Contracts",
                ItemCode: productCode,
                ItemId: productCode
            },
            QuantityOrdered: {
                "$type": "System.Nullable`1[[Asi.Soa.Commerce.DataContracts.QuantityData, Asi.Contracts]], mscorlib",
                Amount: 1
            },
            QuantityShipped: {
                "$type": "System.Nullable`1[[Asi.Soa.Commerce.DataContracts.QuantityData, Asi.Contracts]], mscorlib",
                Amount: 1
            }
        };
        orderData.Lines.$values.push(linePayload);
    }

    return cartData;
}

/**
 * Recalculates and applies updated pricing for a cart's order by invoking the commerce order pricing REST operation.
 * @param cartPayload The cart data whose `ComboOrder.Order` should be repriced; this object's `ComboOrder.Order` is mutated in place with the updated order data.
 * @returns A promise resolving to the same cart payload object with refreshed order pricing.
 */
async function updateCartOrderPricing(cartPayload) {
    let orderData = await RestUtility.restApiSendData("/api/order/_execute", {
        "$type": "Asi.Soa.Commerce.DataContracts.OrderPriceUpdateRequest, Asi.Contracts",
        "EntityTypeName": "Order",
        "OperationName": "UpdatePricing",
        "Order": cartPayload.ComboOrder.Order
    }, "POST", true);
    if (orderData == null) {
        throw new Error(`Failed to update pricing for the order with payload ${JSON.stringify(cartPayload.ComboOrder.Order)}`);
    }
    cartPayload.ComboOrder.Order = orderData;

    return cartPayload;
}

/**
 * Finds the order line item matching a given product code within an order's line items collection.
 * @param orderData The order data object containing a `Lines.$values` collection to search.
 * @param productCode The item code to search for among the order's line items.
 * @returns An object with the matching line item's `lineIndex` (`-1` if not found) and the `lineItem` itself (`undefined` if not found).
 */
function getLineItemByProductCode(orderData: any, productCode: string) {
    let lineIndex = orderData.Lines.$values.findIndex(line => line.Item.ItemCode == productCode);
    let lineItem = orderData.Lines.$values[lineIndex];

    return { lineIndex, lineItem };
}

/**
 * Applies quantity and/or amount overrides from `productData` onto the matching line item within a cart payload, mutating it in place. Clears the line item's `ExtendedAmount` when the amount changes so pricing can be recalculated.
 * @param cartPayload The cart data containing the order whose line item should be updated.
 * @param productCode The item code of the line item to update.
 * @param productData The quantity/amount/gift-status overrides to apply; fields left `null` are not changed.
 * @returns `true` if any field on the line item was updated, otherwise `false`.
 */
function updateProductPayloadQuantityAmount(cartPayload: any, productCode: string, productData: CartProductData) {
    let isUpdated = false;
    let { lineItem } = getLineItemByProductCode(cartPayload.ComboOrder.Order, productCode);
    if (lineItem == null) {
        throw new Error(`Failed to get line item for product code: ${productCode} from cart payload: ${JSON.stringify(cartPayload)}`);
    }

    if (productData.quantity != null) {
        lineItem.QuantityOrdered.Amount = productData.quantity;
        lineItem.QuantityShipped.Amount = productData.quantity;
        isUpdated = true;
    }
    if (productData.amount != null) {
        lineItem.UnitPrice.Amount = productData.amount;
        lineItem.BaseUnitPrice.Amount = productData.amount;
        lineItem.IsUnitPriceOverridden = !productData.isGift;
        // remove the following properties to recalculate the pricing
        delete lineItem.ExtendedAmount;
        isUpdated = true;
    }
    return isUpdated;
}

export {
    addProductToCartByFrame,
    addProductToCart,
    getCartById,
    deleteCartById,
    ensureCartById,
    addGiftToCart,
    deleteProductFromCart,
    // helper methods
    getCartPayloadWithProduct,
    updateCartOrderPricing
};
